/**
 * Available asset list for Flipster FZE (UAE / VARA entity).
 *
 * Source of truth: the Flipster FZE Zendesk help article
 * https://support.flipster.io/hc/en-us/articles/16582074958095-Available-Asset-List-Flipster-FZE
 *
 * There is no backend API for this metadata (launch date / CoinGecko / CertiK
 * links), so it is curated here. Token icons are NOT stored here — they are
 * resolved at runtime from the currency snapshot spec by `symbol`.
 */
/**
 * UI copy for the Available Asset List page, collected in one place.
 *
 * The page is currently Flipster FZE (en-AE / English) only, so the strings are
 * kept as raw text here rather than in the i18n catalogs. When multi-language
 * support is needed, migrate these values to `@flipster/i18n` message keys.
 */
export const availableAssetTexts = {
  pageTitle: 'Available Asset List',
  pageDescription:
    'Explore the digital assets available on Flipster, including their launch dates and verification details.',
  launchDate: 'Launch Date',
  generalInformation: 'General Information',
  auditInformation: 'Audit Information',
} as const;

export interface AvailableAsset {
  /** Display name, e.g. "Bitcoin". */
  name: string;
  /** Ticker symbol, e.g. "BTC". Also used as the icon lookup key. */
  symbol: string;
  /** Launch date, e.g. "Jan 2009". */
  launchDate: string;
  /** General information link (CoinGecko). */
  generalInfoUrl: string;
  /** Additional information link (CertiK audit). Absent for assets without an audit page. */
  auditInfoUrl?: string;
}

export const availableAssets: AvailableAsset[] = [
  {
    name: 'Bitcoin',
    symbol: 'BTC',
    launchDate: 'Jan 2009',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/bitcoin',
  },
  {
    name: 'Ethereum',
    symbol: 'ETH',
    launchDate: 'Jul 2015',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/ethereum',
    auditInfoUrl: 'https://skynet.certik.com/projects/ethereum',
  },
  {
    name: 'Tether',
    symbol: 'USDT',
    launchDate: 'Oct 2014',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/tether',
    auditInfoUrl: 'https://skynet.certik.com/projects/tether',
  },
  {
    name: 'Binance Coin',
    symbol: 'BNB',
    launchDate: 'Jul 2017',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/bnb',
    auditInfoUrl: 'https://skynet.certik.com/projects/binance',
  },
  {
    name: 'Ripple',
    symbol: 'XRP',
    launchDate: 'May 2012',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/xrp',
    auditInfoUrl: 'https://skynet.certik.com/projects/xrp-ledger',
  },
  {
    name: 'Polygon',
    symbol: 'POL',
    launchDate: 'Apr 2019',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/polygon',
    auditInfoUrl: 'https://skynet.certik.com/projects/polygon',
  },
  {
    name: 'Solana',
    symbol: 'SOL',
    launchDate: 'Jan 2018',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/solana',
    auditInfoUrl: 'https://skynet.certik.com/projects/solana',
  },
  {
    name: 'Avalanche',
    symbol: 'AVAX',
    launchDate: 'Mar 2021',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/avalanche',
    auditInfoUrl: 'https://skynet.certik.com/projects/avalanche',
  },
  {
    name: 'Cosmos',
    symbol: 'ATOM',
    launchDate: 'Sep 2020',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/cosmos-hub',
    auditInfoUrl: 'https://skynet.certik.com/projects/cosmos',
  },
  {
    name: 'USDC',
    symbol: 'USDC',
    launchDate: 'Aug 2018',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/usdc',
    auditInfoUrl: 'https://skynet.certik.com/projects/usd-coin',
  },
  {
    name: 'TRON',
    symbol: 'TRX',
    launchDate: 'Apr 2021',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/tron',
    auditInfoUrl: 'https://skynet.certik.com/projects/tron',
  },
  {
    name: 'First Digital USD',
    symbol: 'FDUSD',
    launchDate: 'Apr 2023',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/first-digital-usd',
    auditInfoUrl: 'https://skynet.certik.com/projects/fdusd',
  },
  {
    name: 'NEAR Protocol',
    symbol: 'NEAR',
    launchDate: 'Oct 2020',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/near',
    auditInfoUrl: 'https://skynet.certik.com/projects/near-protocol',
  },
  {
    name: 'Sonic Labs',
    symbol: 'S',
    launchDate: 'Oct 2009',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/sonic',
    auditInfoUrl: 'https://skynet.certik.com/projects/sonic-labs',
  },
  {
    name: 'Algorand',
    symbol: 'ALGO',
    launchDate: 'Oct 2016',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/algorand',
    auditInfoUrl: 'https://skynet.certik.com/projects/algorand',
  },
  {
    name: 'Chainlink',
    symbol: 'LINK',
    launchDate: 'Jun 2013',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/chainlink',
    auditInfoUrl: 'https://skynet.certik.com/projects/chainlink',
  },
  {
    name: 'Kaia',
    symbol: 'KAIA',
    launchDate: 'Jul 2018',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/kaia',
    auditInfoUrl: 'https://skynet.certik.com/projects/kaia',
  },
  {
    name: 'Celo',
    symbol: 'CELO',
    launchDate: 'Mar 2018',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/celo',
    auditInfoUrl: 'https://skynet.certik.com/projects/celo',
  },
  {
    name: 'Sui',
    symbol: 'SUI',
    launchDate: 'Aug 2021',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/sui',
    auditInfoUrl: 'https://skynet.certik.com/projects/sui',
  },
  {
    name: 'Arbitrum',
    symbol: 'ARB',
    launchDate: 'Oct 2018',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/arbitrum',
    auditInfoUrl: 'https://skynet.certik.com/projects/arbitrum',
  },
  {
    name: 'Gram (prev. Toncoin)',
    symbol: 'GRAM',
    launchDate: 'Aug 2020',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/gram',
    auditInfoUrl: 'https://skynet.certik.com/projects/tontoken',
  },
  {
    name: 'Bonk',
    symbol: 'BONK',
    launchDate: 'Aug 2022',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/bonk',
    auditInfoUrl: 'https://skynet.certik.com/projects/bonk1',
  },
  {
    name: 'Conflux',
    symbol: 'CFX',
    launchDate: 'Jan 2019',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/conflux',
    auditInfoUrl: 'https://skynet.certik.com/projects/conflux-network',
  },
  {
    name: 'Tether Gold',
    symbol: 'XAUT',
    launchDate: 'Nov 2014',
    generalInfoUrl: 'https://www.coingecko.com/en/coins/tether-gold',
    auditInfoUrl: 'https://skynet.certik.com/projects/tether-gold',
  },
];
